import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { GET_VOTING_ARTICLE, CREATE_VOTE, UPDATE_VOTE } from '../graphql/voting';
import CommentSection from '../components/CommentSection';
import { formatDate } from '../utils/dateUtils';
import AddToLibraryModal from '../components/AddToLibraryModal';

const VotingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const client = useApolloClient();
  const [selectedOption, setSelectedOption] = useState(null);
  const [isVoting, setIsVoting] = useState(false);
  const [isAddToLibraryModalOpen, setIsAddToLibraryModalOpen] = useState(false);
  const [showSecretInfo, setShowSecretInfo] = useState(false);

  const { loading, error, data, refetch } = useQuery(GET_VOTING_ARTICLE, {
    variables: { id },
    fetchPolicy: 'cache-and-network'
  });

  const [createVote] = useMutation(CREATE_VOTE, {
    onCompleted: (data) => {
      if (data.createVote.success) {
        setIsVoting(false);
        refetch();
      }
    },
    onError: (error) => {
      console.error('投票失敗:', error);
      setIsVoting(false);
    }
  });

  const [updateVote] = useMutation(UPDATE_VOTE, {
    onCompleted: (data) => {
      if (data.updateVote.success) {
        setIsVoting(false);
        refetch();
      }
    },
    onError: (error) => {
      console.error('更新投票失敗:', error);
      setIsVoting(false);
    }
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-4">載入投票失敗</div>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            重新載入
          </button>
        </div>
      </div>
    );
  }

  const article = data?.votingArticle?.article;

  if (!article) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <div className="text-gray-600 text-lg mb-4">投票不存在</div>
          <button 
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            返回
          </button>
        </div>
      </div>
    );
  }

  const userVote = article.userVote;
  const totalVotes = article.voteResults?.reduce((sum, result) => sum + result.count, 0) || 0;

  const handleVote = async (optionIndex) => {
    if (isVoting || (userVote && userVote.selectedOption === optionIndex)) return;
    
    setIsVoting(true);
    setSelectedOption(optionIndex);
    
    try {
      if (userVote) {
        await updateVote({
          variables: {
            input: {
              articleId: article.id,
              selectedOption: optionIndex
            }
          }
        });
      } else {
        await createVote({
          variables: {
            input: {
              articleId: article.id,
              selectedOption: optionIndex
            }
          }
        });
      }
    } catch (error) {
      console.error('投票錯誤:', error);
      setIsVoting(false);
      setSelectedOption(null);
    }
  };

  const handleAddToLibraryClick = () => {
    setIsAddToLibraryModalOpen(true);
  };

  const handleAddToLibrarySuccess = () => {
    client.cache.evict({ fieldName: 'votingArticles' });
    client.cache.gc();
  };

  const calculatePercentage = (count, total) => {
    if (total === 0) return '0.00';
    return ((count / total) * 100).toFixed(2);
  };

  return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* 返回按鈕 */}
          <div className="mb-6">
            <button 
              onClick={() => window.history.back()}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <span className="mr-2">←</span>
              返回投票列表
            </button>
          </div>

          {/* 投票詳情 - 完整顯示 */}
          <div className="mb-8">
            <div className={`bg-white rounded-lg shadow-md ${
              article.question?.secret 
                ? 'border-2 border-yellow-400 shadow-yellow-100' 
                : 'border border-gray-100'
            }`}>
              {/* 祕密問題提示條 - 只在點擊後顯示 */}
              {article.question?.secret && showSecretInfo && (
                <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-b border-yellow-200 px-6 py-3">
                  <div className="flex items-center justify-between text-sm text-yellow-800">
                    <div className="flex items-center">
                      <span className="mr-2">🔒</span>
                      <span className="font-medium">祕密問題：不會顯示在配對詳情和共同話題中，僅參與配對分數計算</span>
                    </div>
                    <button 
                      onClick={() => setShowSecretInfo(false)}
                      className="text-yellow-600 hover:text-yellow-800 ml-2"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              )}
              
              {/* 標題區域 - 完整顯示 */}
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-start gap-2 mb-3">
                  <h1 className="text-2xl font-bold text-gray-900 break-words flex-1">
                    {article.question?.title || '未知問題'}
                  </h1>
                  {article.question?.secret && (
                    <button
                      onClick={() => setShowSecretInfo(!showSecretInfo)}
                      className="inline-flex items-center px-2 py-1 rounded text-sm font-medium bg-yellow-100 text-yellow-800 border border-yellow-300 hover:bg-yellow-200 transition-colors cursor-pointer"
                    >
                      🔒 祕密
                    </button>
                  )}
                </div>
                {article.question?.description && (
                  <p className="text-base text-gray-700 mb-4 whitespace-pre-wrap break-words">
                    {article.question.description}
                  </p>
                )}
                {/* 標籤顯示 */}
                {article.question?.tags && article.question.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {article.question.tags.map((tag, index) => (
                      <span
                        key={tag?.id || index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800"
                      >
                        {tag?.name || tag}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex items-center text-sm text-gray-500 space-x-4">
                  <span>🆔 {article.id}</span>
                  {article.isAnonymous ? (
                    <span className="flex items-center">
                      <span className="mr-1">🎭</span>
                      <span>匿名使用者</span>
                    </span>
                  ) : article.creator?.id ? (
                    <span
                      onClick={() => navigate(`/user/${article.creator.id}`)}
                      className="cursor-pointer hover:text-blue-600 hover:underline transition-colors"
                    >
                      👤 {article.creator.username || '匿名'}
                    </span>
                  ) : (
                    <span>👤 {article.creator?.username || '匿名'}</span>
                  )}
                  <span>📅 {formatDate(article.createdAt)}</span>
                  <span>💬 {article.commentCount || 0} 則留言</span>
                </div>
              </div>

              {/* 投票選項區域 */}
              <div className="p-6">
                {article.question && (
                  <div className="mb-4">
                    {/* 選項列表 */}
                    <div className="space-y-3">
                      {article.question.options?.map((option, index) => {
                        const voteResult = article.voteResults?.find(r => r.optionIndex === index);
                        const voteCount = voteResult?.count || 0;
                        const percentage = calculatePercentage(voteCount, totalVotes);
                        const isUserVoted = userVote?.selectedOption === index;
                        const isSelected = selectedOption === index;
                        
                        return (
                          <div key={index} className="relative overflow-hidden">
                            <button
                              onClick={() => handleVote(index)}
                              disabled={isVoting || (userVote && userVote.selectedOption === index)}
                              className={`w-full p-4 text-left rounded-lg border-2 transition-all duration-200 relative ${
                                isUserVoted
                                  ? 'border-yellow-400'
                                  : isSelected
                                  ? 'border-purple-400'
                                  : 'border-gray-200 hover:border-gray-300'
                              } ${isVoting ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                            >
                              {/* 進度條背景 */}
                              {article.showVoteResults !== false && totalVotes > 0 && (
                                <div 
                                  className={`absolute inset-0 transition-all duration-300 ${
                                    isUserVoted ? 'bg-yellow-100' : 'bg-gray-100'
                                  }`}
                                  style={{ width: `${percentage}%` }}
                                ></div>
                              )}
                              
                              <div className="flex items-start justify-between relative z-10">
                                <span className="font-semibold text-lg break-words flex-1 mr-4">{option.text}</span>
                                <div className="flex items-center space-x-3 flex-shrink-0">
                                  {userVote && isUserVoted && (
                                    <span className="text-blue-700 font-medium">
                                      ✓ 已投票
                                    </span>
                                  )}
                                  {/* 根據 showVoteResults 標誌顯示/隱藏票數分佈 */}
                                  {article.showVoteResults !== false && (
                                    <span className="text-base text-gray-700 font-medium">
                                      {voteCount} 票 {percentage}%
                                    </span>
                                  )}
                                </div>
                              </div>
                            </button>
                            
                            {isVoting && isSelected && (
                              <div className="absolute inset-0 flex items-center justify-center bg-purple-50 rounded-lg">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400"></div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* 底部統計 */}
              <div className="px-6 py-4 bg-gray-50 rounded-b-lg border-t border-gray-100">
                <div className="flex items-center justify-between text-sm gap-2">
                  <div className="flex items-center space-x-2 min-w-0">
                    <span className="text-gray-700 font-medium whitespace-nowrap">🗳️ 總投票: {totalVotes}</span>
                  </div>
                  <div className="flex items-center space-x-3 flex-shrink-0">
                    <button 
                      onClick={handleAddToLibraryClick}
                      className="px-4 py-2 rounded-lg bg-gradient-to-r from-pink-400 to-purple-500 text-white hover:from-pink-500 hover:to-purple-600 transition-all font-medium shadow-md hover:shadow-lg whitespace-nowrap"
                    >
                      加入擇友標準
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <AddToLibraryModal
            isOpen={isAddToLibraryModalOpen}
            onClose={() => setIsAddToLibraryModalOpen(false)}
            onSuccess={handleAddToLibrarySuccess}
            question={article.question}
          />

          {/* 評論區 - 只在 allowComments 為 true 時顯示 */}
          {article.allowComments !== false && (
            <div className="mb-8">
              <CommentSection articleId={article.id} />
            </div>
          )}
          
          {/* 如果不允許評論，顯示提示信息 */}
          {article.allowComments === false && (
            <div className="mb-8 bg-gray-100 rounded-lg p-6 text-center">
              <p className="text-gray-600 text-lg font-medium">此投票不允許留言</p>
            </div>
          )}

          {/* 分享功能 */}
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">分享這個投票</h3>
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  alert('連結已複製到剪貼簿！');
                }}
                className="px-4 py-2 bg-purple-400 text-white rounded-lg hover:bg-purple-500"
              >
                📋 複製連結
              </button>
              <button 
                onClick={() => {
                  const text = `來看看這個有趣的投票：${article.question?.title || '投票'}`;
                  const url = window.location.href;
                  const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
                  window.open(shareUrl, '_blank');
                }}
                className="px-4 py-2 bg-purple-400 text-white rounded-lg hover:bg-purple-500"
              >
                🐦 分享到 Twitter
              </button>
              <button 
                onClick={() => {
                  const url = window.location.href;
                  const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
                  window.open(shareUrl, '_blank');
                }}
                className="px-4 py-2 bg-purple-400 text-white rounded-lg hover:bg-purple-500"
              >
                📘 分享到 Facebook
              </button>
            </div>
          </div>
        </div>
      </div>
  );
};

export default VotingDetail; 